import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Upload, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { DISTRICTS } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Toranam } from "@/components/Ornaments";
import FileUpload from "@/components/FileUpload";
import FreeTextSelect from "@/components/FreeTextSelect";
import { toast } from "sonner";

const init = {
  name: "", deity: "", village: "", mandal: "", district: "", state: "Andhra Pradesh",
  map_lat: "", map_lng: "", map_link: "", temple_photos: [], quarters_photos: [],
  salary: "", ddn: false, free_food: false, morning_shift: "", evening_shift: "",
  afternoon_note: "", festival_note: "", accommodation: false, house_type: "",
  water: false, power_backup: false, pooja_room: false, attached_restroom: false,
  archaka_preference: "none", village_purohitam: "not_allowed", contact_name: "", contact_phone: "", description: "",
};

export default function PostVacancy() {
  const { t, settings } = useApp();
  const { user } = useAuth();
  const nav = useNavigate();
  const phone = settings?.helpline_phone || "9603333685";
  const [tab, setTab] = useState("self");
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState("");

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!user) return nav("/auth?mode=login&next=/post-vacancy");
    if (user.role === "archaka") return toast.error("అర్చకులు ఖాళీలను పోస్ట్ చేయలేరు. దేవాలయ ఖాతా అవసరం.");
    if (!f.name || !f.deity || !f.village || !f.mandal || !f.district || !f.contact_phone)
      return toast.error("తప్పనిసరి వివరాలు నింపండి");
    setBusy(true); setDup("");
    try {
      const payload = { ...f, salary: parseInt(f.salary) || 0, map_lat: f.map_lat ? parseFloat(f.map_lat) : null, map_lng: f.map_lng ? parseFloat(f.map_lng) : null };
      await api.post("/temples", payload);
      toast.success("దేవాలయ ఖాళీ విజయవంతంగా పోస్ట్ చేయబడింది! 🎉");
      nav("/temples");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.duplicate) setDup(d.message);
      else toast.error(typeof d === "string" ? d : "పోస్ట్ చేయడంలో లోపం");
    } finally { setBusy(false); }
  };

  return (
    <div className="parchment-texture min-h-screen">
      <div className="bg-gradient-to-r from-maroon to-saffron py-10 text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">📢 {t("post_vacancy")}</h1>
        <p className="text-white/85 font-telugu mt-2">మీ దేవాలయ అర్చక ఖాళీని ప్రకటించండి</p>
      </div>
      <Toranam />

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Mode selector */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button data-testid="mode-self" onClick={() => setTab("self")} className={`p-4 rounded-xl border-2 font-telugu text-sm font-semibold ${tab === "self" ? "border-saffron bg-marigold/15 text-saffron" : "border-goldline/40 text-maroon"}`}>
            <Upload className="w-5 h-5 mx-auto mb-1" />{t("self_upload")}
          </button>
          <button data-testid="mode-assisted" onClick={() => setTab("assisted")} className={`p-4 rounded-xl border-2 font-telugu text-sm font-semibold ${tab === "assisted" ? "border-saffron bg-marigold/15 text-saffron" : "border-goldline/40 text-maroon"}`}>
            <Phone className="w-5 h-5 mx-auto mb-1" />{t("assisted")}
          </button>
        </div>

        {tab === "assisted" ? (
          <div className="bg-surface rounded-2xl gold-border temple-shadow p-8 text-center space-y-4">
            <div className="text-4xl">🙏</div>
            <h3 className="font-serifte font-bold text-maroon text-xl">కాల్ / వాట్సాప్ ద్వారా సహాయం</h3>
            <p className="font-telugu text-muted-foreground">ఫారం నింపడం కష్టంగా ఉందా? మా బృందానికి కాల్ చేయండి లేదా వాట్సాప్ చేయండి. మేము మీ దేవాలయ వివరాలను నమోదు చేస్తాము.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <a data-testid="assisted-call" href={`tel:${phone}`}><Button className="btn-sacred border-0 font-telugu w-full"><Phone className="w-4 h-4 mr-2" />{t("call_now")}: {phone}</Button></a>
              <a data-testid="assisted-wa" href={`https://wa.me/91${phone}?text=${encodeURIComponent("దేవాలయ ఖాళీ నమోదు చేయాలి")}`} target="_blank" rel="noreferrer"><Button className="bg-[#25D366] hover:bg-[#1da851] text-white border-0 font-telugu w-full"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</Button></a>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {dup && (
              <div data-testid="duplicate-banner" className="p-4 rounded-xl bg-red-50 border-2 border-red-400 flex gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 font-telugu leading-relaxed">{dup}</p>
              </div>
            )}
            <Section title="🛕 దేవాలయ వివరాలు">
              <Field label="దేవాలయం పేరు *"><Input data-testid="pv-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="gold-border font-telugu" /></Field>
              <Field label="మూలవిరాట్టు / దేవత *"><Input data-testid="pv-deity" value={f.deity} onChange={(e) => set("deity", e.target.value)} className="gold-border font-telugu" /></Field>
              <Field label="గ్రామం *"><Input data-testid="pv-village" value={f.village} onChange={(e) => set("village", e.target.value)} className="gold-border font-telugu" /></Field>
              <Field label="మండలం *"><Input data-testid="pv-mandal" value={f.mandal} onChange={(e) => set("mandal", e.target.value)} className="gold-border font-telugu" /></Field>
              <Field label="జిల్లా *">
                <FreeTextSelect testid="pv-district" value={f.district} onChange={(v) => set("district", v)} options={DISTRICTS} placeholder="జిల్లా ఎంచుకోండి లేదా టైప్ చేయండి" />
              </Field>
              <Field label="రాష్ట్రం">
                <Select value={f.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="pv-state" className="gold-border font-telugu"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Andhra Pradesh">ఆంధ్రప్రదేశ్</SelectItem><SelectItem value="Telangana">తెలంగాణ</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="గూగుల్ మ్యాప్ లింక్"><Input data-testid="pv-maplink" value={f.map_link} onChange={(e) => set("map_link", e.target.value)} className="gold-border" placeholder="https://maps.google.com/..." /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Latitude"><Input value={f.map_lat} onChange={(e) => set("map_lat", e.target.value)} className="gold-border" placeholder="13.55" /></Field>
                <Field label="Longitude"><Input value={f.map_lng} onChange={(e) => set("map_lng", e.target.value)} className="gold-border" placeholder="79.35" /></Field>
              </div>
            </Section>

            <Section title="📷 ఫోటోలు">
              <FileUpload testid="pv-temple-photos" label="దేవాలయం / గర్భగుడి ఫోటోలు" multiple value={f.temple_photos} onChange={(v) => set("temple_photos", v)} />
              <div className="mt-3"><FileUpload testid="pv-quarters-photos" label="అర్చక క్వార్టర్స్ ఫోటోలు" multiple value={f.quarters_photos} onChange={(v) => set("quarters_photos", v)} /></div>
            </Section>

            <Section title="💰 వేతనం & ప్రయోజనాలు">
              <Field label="నెలవారీ వేతనం (₹)"><Input data-testid="pv-salary" type="number" value={f.salary} onChange={(e) => set("salary", e.target.value)} className="gold-border" /></Field>
              <Toggle label="🪔 ధూప దీప నైవేద్యం (DDN)" checked={f.ddn} onChange={(v) => set("ddn", v)} testid="pv-ddn" />
              <Toggle label="🍲 ఉచిత భోజనం" checked={f.free_food} onChange={(v) => set("free_food", v)} testid="pv-food" />
            </Section>

            <Section title="🕐 విధి వేళలు">
              <Field label="ఉదయం షిఫ్ట్"><Input data-testid="pv-morning" value={f.morning_shift} onChange={(e) => set("morning_shift", e.target.value)} className="gold-border" placeholder="5:00 AM - 11:00 AM" /></Field>
              <Field label="సాయంత్రం షిఫ్ట్"><Input data-testid="pv-evening" value={f.evening_shift} onChange={(e) => set("evening_shift", e.target.value)} className="gold-border" placeholder="4:00 PM - 8:30 PM" /></Field>
              <Field label="మధ్యాహ్న విశ్రాంతి గమనిక"><Input value={f.afternoon_note} onChange={(e) => set("afternoon_note", e.target.value)} className="gold-border font-telugu" /></Field>
              <Field label="పండుగ విధుల గమనిక"><Input value={f.festival_note} onChange={(e) => set("festival_note", e.target.value)} className="gold-border font-telugu" /></Field>
            </Section>

            <Section title="🏠 క్వార్టర్స్ వివరాలు">
              <Toggle label="నివాస వసతి కలదు" checked={f.accommodation} onChange={(v) => set("accommodation", v)} testid="pv-accom" />
              {f.accommodation && (
                <>
                  <Field label="ఇంటి రకం">
                    <Select value={f.house_type} onValueChange={(v) => set("house_type", v)}>
                      <SelectTrigger data-testid="pv-housetype" className="gold-border font-telugu"><SelectValue placeholder="ఎంచుకోండి" /></SelectTrigger>
                      <SelectContent><SelectItem value="1BHK">1BHK</SelectItem><SelectItem value="2BHK">2BHK</SelectItem><SelectItem value="Compound House">కాంపౌండ్ ఇల్లు</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Toggle label="💧 నీరు" checked={f.water} onChange={(v) => set("water", v)} testid="pv-water" />
                    <Toggle label="⚡ పవర్ బ్యాకప్" checked={f.power_backup} onChange={(v) => set("power_backup", v)} testid="pv-power" />
                    <Toggle label="🕉️ పూజ గది" checked={f.pooja_room} onChange={(v) => set("pooja_room", v)} testid="pv-pooja" />
                    <Toggle label="🚿 అటాచ్డ్ బాత్రూమ్" checked={f.attached_restroom} onChange={(v) => set("attached_restroom", v)} testid="pv-bath" />
                  </div>
                </>
              )}
            </Section>

            <Section title="👨‍👩‍👦 అర్చక ప్రాధాన్యత">
              <Label className="font-telugu text-maroon text-sm">మీ దేవాలయానికి ఎలాంటి అర్చక స్వామిని కోరుకుంటున్నారు?</Label>
              <RadioGroup value={f.archaka_preference} onValueChange={(v) => set("archaka_preference", v)} className="mt-2 space-y-2" data-testid="pv-pref">
                {[["family", "కుటుంబ సమేత అర్చకుడు కావలెను"], ["bachelor", "ఒంటరి / బ్యాచిలర్ అర్చకుడు కావలెను"], ["none", "ఎవరైనా పర్వాలేదు / నిబంధన లేదు"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 p-2 rounded-lg border border-goldline/40 cursor-pointer hover:bg-marigold/5">
                    <RadioGroupItem value={v} id={`pref-${v}`} /><span className="font-telugu text-sm text-maroon">{l}</span>
                  </label>
                ))}
              </RadioGroup>
              <div className="mt-3"><Label className="font-telugu text-maroon text-sm">🙏 గ్రామ పురోహితం అనుమతి</Label>
                <Select value={f.village_purohitam} onValueChange={(v) => set("village_purohitam", v)}>
                  <SelectTrigger data-testid="pv-purohitam" className="gold-border font-telugu mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="allowed">అనుమతి కలదు</SelectItem><SelectItem value="not_allowed">అనుమతి లేదు</SelectItem><SelectItem value="after_hours">దేవాలయ వేళల తర్వాత</SelectItem></SelectContent>
                </Select>
              </div>
            </Section>

            <Section title="📞 సంప్రదింపు వివరాలు">
              <Field label="సంప్రదింపు వ్యక్తి పేరు"><Input data-testid="pv-contactname" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="gold-border font-telugu" /></Field>
              <Field label="ఫోన్ నంబర్ *"><Input data-testid="pv-contactphone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="gold-border" maxLength={10} /></Field>
              <Field label="వివరణ"><Textarea data-testid="pv-desc" value={f.description} onChange={(e) => set("description", e.target.value)} className="gold-border font-telugu" rows={3} /></Field>
            </Section>

            <Button data-testid="pv-submit" onClick={submit} disabled={busy} className="w-full btn-sacred border-0 font-telugu text-base py-6">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `🛕 ${t("submit")}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-surface rounded-2xl gold-border temple-shadow p-5">
    <h3 className="font-serifte font-bold text-maroon text-lg mb-3">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);
const Field = ({ label, children }) => (<div><Label className="font-telugu text-maroon text-sm">{label}</Label><div className="mt-1">{children}</div></div>);
const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between p-2 rounded-lg border border-goldline/30">
    <span className="font-telugu text-sm text-maroon">{label}</span>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);
